import { useEffect, useRef, useState } from 'react';
import { Backpack, Camera, Car, ClipboardList, NotebookPen, UsersRound } from 'lucide-react';
import type { EventDetail } from '../api/events';
import type { EventDocket as Docket } from '../api/eventDocket';
import { loadEventDocket } from '../api/eventDocket';
import { DocketRow } from '../ui/DocketRow';
import { EventGallery } from './EventGallery';
import { EventPacking } from './EventPacking';
import { EventProgram } from './EventProgram';
import { EventRequirements } from './EventRequirements';
import { EventRsvp } from './EventRsvp';
import { EventTransport } from './EventTransport';
import { FamilyPacking } from './FamilyPacking';
import type { Child } from '../api/auth';

/**
 * The spine — TROOP-116.
 *
 * Five sections used to stand fully expanded between the event's facts and its thread, each one
 * mounting and fetching on sight. An officer opening an ordinary campout met five management
 * surfaces and about seventeen requests before reaching the conversation. This is the same five as
 * rows that answer themselves.
 *
 * **A closed row states its answer.** "3 of 8", "6 seats, 6 spare". Somebody can tell whether they
 * are sorted for a trip without opening anything, which is the actual job — a row reading only
 * "Packing" would have moved information behind a click and called it tidier.
 *
 * **A row opens on arrival only when it is waiting on this person.** That is `GOING` when somebody
 * they answer for has not replied, and `FORMS` when their family still owes something. Everything
 * else starts closed. Two rules, both about an unanswered question rather than about importance:
 * a task you work through is not the same as a question you owe an answer to.
 *
 * **The rows are deliberately not tabs, and what is inside them deliberately is.** The repo has
 * already reasoned the first half out for `FamilyPacking` — "a parent packing two bags on a
 * Thursday works through both rather than choosing between them, and a tab bar hides the second
 * behind a click that says nothing about what is in it". That argument is about things somebody
 * works *through*; Yours and Everyone are two audiences for the same subject, and nobody is both
 * at once. So the spine stays rows and each panel opens onto `PanelTabs` (TROOP-137).
 *
 * The initial state is computed once, when the payload lands, and a person's own toggle wins after
 * that. It is not persisted: an unanswered RSVP should still be asking on the next visit.
 */
export function EventDocket({
  unitId,
  event,
  meId,
  children,
  subgroupLabelSingular,
  subgroupLabelPlural,
  onOpenSummary,
  onEventChanged,
}: {
  /** Undefined on `/family` — that absence picks the guardian's door throughout. */
  unitId?: string;
  event: EventDetail;
  meId: string;
  /** The caller's own children, for the family door's per-child controls. */
  children?: Child[];
  /**
   * The unit's own word for a group — Patrol, Den, Crew. Rendered, never assumed (rule 16).
   *
   * The member's door only: they are `EventPacking`'s, and a guardian gets `FamilyPacking`, which
   * names no groups. Passing the plural twice to satisfy a required prop would have put "Patrols"
   * where a screen says "Patrol", which is the kind of wrong that reads as a typo forever.
   */
  subgroupLabelSingular?: string;
  subgroupLabelPlural?: string;
  /**
   * Opens the trip summary — the same table these panels draw, with every column at once, the
   * filters and the paper copy. Passed rather than navigated to from here: the member's door has
   * one and `/family` has none, and a component that built the URL itself would be the second
   * place deciding a guardian has no leader's screen.
   */
  onOpenSummary?: () => void;
  /**
   * Re-reads the event. The program lives on the event rather than behind a route of its
   * own, so saving it has to reach the screen holding that payload -- otherwise the panel
   * updates and the subtitle above it does not.
   */
  onEventChanged?: () => void;
}) {
  const [docket, setDocket] = useState<Docket | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // A latch, not state: nothing renders from it, and making it state would re-run the effect.
  const seeded = useRef(false);
  const [photoCount, setPhotoCount] = useState<number | null>(null);

  const reload = () => {
    void loadEventDocket(unitId, event.id).then((d) => d && setDocket(d));
  };

  useEffect(() => {
    let live = true;

    loadEventDocket(unitId, event.id)
      .then((d) => {
        if (!live || !d) return;
        setDocket(d);

        // Once, on arrival. After this the map belongs to whoever is reading.
        if (seeded.current) return;
        seeded.current = true;

        setOpen({
          going: (d.going?.mineAnswered ?? 0) < (d.going?.mineTotal ?? 0),
          forms: (d.forms?.mineOutstanding ?? 0) > 0,
          program: (d.program?.yoursOutstanding ?? 0) > 0,
        });
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [unitId, event.id]);

  if (!docket) return null;

  const toggle = (id: string) => setOpen((was) => ({ ...was, [id]: !was[id] }));
  const family = unitId === undefined;
  const mine = children ?? [];

  return (
    <div>
      {/* First, and deliberately. The other rows are questions the event asks of a reader;
          this one is what the event is actually going to be. Somebody opening a Monday
          meeting wants "knots tonight" before "you have not said whether you are coming". */}
      {docket.program && (
        <DocketRow
          label="Program"
          icon={NotebookPen}
          value={programValue(docket)}
          open={Boolean(open.program)}
          onToggle={() => toggle('program')}
        >
          <EventProgram
            unitId={unitId}
            event={event}
            canWrite={docket.program.canWrite}
            onSaved={() => {
              reload();
              onEventChanged?.();
            }}
          />
        </DocketRow>
      )}

      {docket.going && (
        <DocketRow
          label="Going"
          icon={UsersRound}
          value={goingValue(docket)}
          open={Boolean(open.going)}
          onToggle={() => toggle('going')}
        >
          <EventRsvp
            unitId={unitId}
            eventId={event.id}
            meId={meId}
            children={mine}
            canAnswerForOthers={docket.going.canAnswerForOthers}
            onAnswered={reload}
            onOpenSummary={onOpenSummary}
          />
        </DocketRow>
      )}

      {docket.forms && (
        <DocketRow
          label="Forms"
          icon={ClipboardList}
          value={formsValue(docket)}
          open={Boolean(open.forms)}
          onToggle={() => toggle('forms')}
        >
          <EventRequirements
            unitId={unitId}
            eventId={event.id}
            meId={meId}
            children={mine.map((c) => ({ personId: c.personId, displayName: c.displayName }))}
            canManage={docket.forms.canManage}
            zone={event.timeZoneId}
            onChanged={reload}
            onOpenSummary={onOpenSummary}
          />
        </DocketRow>
      )}

      {docket.bring && (
        <DocketRow
          label="Bring"
          icon={Backpack}
          value={bringValue(docket)}
          open={Boolean(open.bring)}
          onToggle={() => toggle('bring')}
        >
          {/* Two panels, because a parent packs for their children and a member packs for
              themselves. Same rule, same policy class, different subject. */}
          {family ? (
            <FamilyPacking eventId={event.id} children={mine} rsvps={null} onChanged={reload} />
          ) : (
            <EventPacking
              unitId={unitId!}
              eventId={event.id}
              meId={meId}
              canManage={docket.bring.canManage}
              subgroupLabelSingular={subgroupLabelSingular ?? ''}
              subgroupLabelPlural={subgroupLabelPlural ?? ''}
              onChanged={reload}
            />
          )}
        </DocketRow>
      )}

      {docket.travel && (
        <DocketRow
          label="Travel"
          icon={Car}
          value={travelValue(docket)}
          open={Boolean(open.travel)}
          onToggle={() => toggle('travel')}
        >
          {/* The event's own times and place go down as well as the zone: they are what the
              round-trip prompt is prefilled from on an event that says it departs and holds no trips
              yet (TROOP-241). The place resolves the way the Where row does — the name, or the
              address when there is no name. */}
          <EventTransport
            unitId={unitId ?? null}
            eventId={event.id}
            timeZoneId={event.timeZoneId}
            departsAt={event.departsAt}
            returnsAt={event.returnsAt}
            place={event.locationName ?? event.locationAddress}
            canManage={docket.travel.canManage}
            people={
              family
                ? [
                    ...mine.map((c) => ({ personId: c.personId, displayName: c.displayName })),
                    { personId: meId, displayName: 'You' },
                  ]
                : undefined
            }
            onChanged={reload}
          />
        </DocketRow>
      )}

      {docket.photos && (
        <DocketRow
          label="Photos"
          icon={Camera}
          value={photosValue(docket, photoCount)}
          open={Boolean(open.photos)}
          onToggle={() => toggle('photos')}
        >
          <EventGallery
            unitId={unitId}
            eventId={event.id}
            zone={event.timeZoneId}
            // The server's answer, off the one read this page is made of (TROOP-225). Never
            // recomposed here from a permission level: a guardian holds none, and the question
            // reads their classification too.
            canAdd={docket.photos.canAdd}
            uploadAudience={docket.photos.uploadAudience}
            onCountChange={setPhotoCount}
          />
        </DocketRow>
      )}
    </div>
  );
}

/**
 * The closed values. Each one is the answer somebody came for, in the fact table's register.
 *
 * Written as functions rather than a lookup because they are audience-shaped: the same row says a
 * different true thing to a parent and to a leader, which is the access rules showing through the
 * layout rather than being bolted onto it.
 */
function goingValue({ going }: Docket): string {
  if (!going) return '';

  // A question owed comes first: somebody has not answered, and that is what the row is for.
  if (going.mineAnswered < going.mineTotal) {
    const missing = going.mineTotal - going.mineAnswered;
    return going.mineTotal === 1
      ? "You haven't said"
      : `${missing} of yours ${missing === 1 ? 'has' : 'have'} not said`;
  }

  // Silence is its own state and never folded into "not going" -- a roll-up that hid it would tell
  // a family everybody who has not opened the app is staying home.
  return going.noResponse > 0
    ? `${going.going} going · ${going.noResponse} yet to say`
    : `${going.going} going`;
}

function formsValue({ forms }: Docket): string {
  if (!forms) return '';

  if (forms.asked === 0) return 'Nothing asked yet';

  if (forms.mineOutstanding > 0) {
    return forms.mineOutstanding === 1
      ? '1 still to do'
      : `${forms.mineOutstanding} still to do`;
  }

  return 'All done';
}

/**
 * What this caller is bringing. Never what anybody else is.
 *
 * **No leader roll-up.** "0 of 2 ready" divided the Scouts who had opened a list by every Scout in
 * the unit, and a packing list is opt-in — so the gap measured how many people had chosen to use
 * the feature, not how ready the troop was. A leader cannot act on that, and it was the number
 * sitting on the row a family reads first. The packing panel still offers an officer the count if
 * they open it, which is where somebody who wants it has asked for it.
 *
 * **The word "packed" appears nowhere here, and that is the architecture rule rather than taste.**
 * The product says "what to bring" precisely so the feature is not a campout feature: a Monday
 * meeting is not packed for, and the same list serves somebody bringing snacks to one. So the row
 * counts and the label carries the verb — "BRING · 3 of 8" — rather than saying "3 of 8 packed"
 * and quietly narrowing the whole thing to trips with tents.
 *
 * Bounded at two names. Smoke residue has given one seeded parent sixty-six children in this unit,
 * most of them called Rowan, and they are separate person ids, so deduplicating fixes nothing —
 * anything per-child needs a bound whatever the data looks like.
 */
function bringValue({ bring }: Docket): string {
  if (!bring) return '';

  if (bring.mine.length === 0) {
    // Two different nothings, and they read differently to somebody deciding whether to open the
    // panel. Never "0 of 0", which is a statement about an empty list rather than about them.
    return bring.lists === 0 ? 'Nothing required' : 'Nothing yet';
  }

  if (bring.mine.length <= 2) {
    return bring.mine
      .map((l) => (bring.mine.length === 1 ? sentence(brought(l)) : `${firstName(l.displayName)} ${brought(l)}`))
      .join(' · ');
  }

  const checked = bring.mine.reduce((sum, l) => sum + l.checked, 0);
  const total = bring.mine.reduce((sum, l) => sum + l.total, 0);

  return `${bring.mine.length} lists · ${checked} of ${total}`;
}

/** "all 5" when there is nothing left to find, "3 of 8" while there is. Never a verb. */
const brought = (l: { checked: number; total: number }) =>
  l.checked === l.total ? `all ${l.total}` : `${l.checked} of ${l.total}`;

const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Just the given name. "Rowan Lindqvist 3 of 8" does not fit a fact value. */
const firstName = (name: string) => name.split(' ')[0];

/**
 * Covered, or not, across every leg.
 *
 * **A seat count belongs to a leg, so the closed row does not carry one.** This subtracted a total
 * of riders from a total of seats, which let a surplus on one trip cancel a shortfall on another:
 * twelve seats offered on the outbound and none at all on the return read "12 seats · full" while
 * nobody was driving anybody home, and the number was wrong in the reassuring direction. The server
 * counts short legs now; the row reports coverage and the panel below it keeps a real seat line per
 * leg, which is where a number like that means something.
 *
 * A shortfall is still a state rather than an error -- every trip is short until the Wednesday.
 *
 * **On a round trip it names the direction rather than counting** (TROOP-239). "Short on 1 of 2
 * trips" is arithmetic about a plan the leader thinks of as one journey; "short on the way home" is
 * the same fact in the words they would use, and it is still two answers rather than one -- the two
 * directions are asked separately and neither is ever added to the other.
 */
function travelValue({ travel }: Docket): string {
  if (!travel) return '';

  if (travel.legs === 0) return 'No trips yet';
  if (travel.seatsOffered === 0) return 'Nobody driving yet';

  if (travel.legsShort === 0) return 'Covered';

  // Each direction answered against its own ledger line, all the way from the server. Both short is
  // two facts and says so; it is not a third state and no number spans the pair.
  if (travel.roundTrip) {
    const { thereIsShort, homeIsShort } = travel.roundTrip;

    if (thereIsShort && homeIsShort) return 'Short both ways';
    if (homeIsShort) return 'Short on the way home';
    if (thereIsShort) return 'Short on the way there';
  }

  // With one leg there is no ambiguity about which trip is short, so say by how much. With more,
  // naming the count of trips is the honest summary -- the seats are in the panel, per leg.
  if (travel.legs === 1) return `Short by ${travel.seatsShort}`;

  return `Short on ${travel.legsShort} of ${travel.legs} trips`;
}

/**
 * What this one is about, or that nobody has said yet.
 *
 * **The theme, not a count.** Every other row here states a number because there is a number
 * worth stating; this row's honest answer is the line naming what is happening. Inventing
 * "7 of 9 duties assigned" to make it match the shape of its neighbours would be a metric
 * nobody asked for and, once duties exist, a per-row roll-up of exactly the kind TROOP-135
 * showed reads healthy over a broken part.
 *
 * The two empty states differ by audience, which is the access rules showing through the
 * layout: for an officer this is the thing to do next, for everybody else it is a fact about
 * the event.
 */
function programValue({ program }: Docket): string {
  if (!program) return '';

  // A question owed comes first, exactly as it does on Going: something is down to you and
  // that is what the row is for. The theme is what the evening IS, which matters second.
  if (program.yoursOutstanding > 0) {
    const owed =
      program.yoursOutstanding === 1
        ? '1 job is yours'
        : `${program.yoursOutstanding} jobs are yours`;

    return program.theme ? `${program.theme} \u00b7 ${owed}` : owed;
  }

  if (program.theme) return program.theme;
  if (program.hasPlan) return 'Planned';

  return program.canWrite ? 'Not planned yet' : 'Nothing planned yet';
}

/** The live count wins once the panel has been opened, so an upload is reflected without a reload. */
function photosValue({ photos }: Docket, live: number | null): string {
  if (!photos) return '';
  if (!photos.enabled) return 'Turned off';

  const count = live ?? photos.count;

  return count === 0 ? 'None yet' : String(count);
}
